// src/components/Dashboard/CheckCard.jsx
//
// Un cheque del seguro dentro de la Fase 4 del reclamo, con su propio
// recorrido (config/claimFlow.js → CHECK_FLOW, documento del cliente):
//   Emitido → Ruta de endoso (PA) → IINK | Endoso de mortgage | directo
//   → Depositado → Breakdown (PA) → Closing Statement → Desembolso
//   → Cobro CSR (interno).
// Arriba, los datos del cheque (editables por el equipo CSR); después la
// línea de pasos (se puede abrir cualquiera para ver o corregir sus datos) y
// el paso actual con su plazo, lo que falta y los botones para avanzar.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { CHECK_FLOW, STEP_HELP } from '../../config/claimFlow';
import { getBlockers, getOptionBlockers, getStepTimer, MORTGAGE_DOCS, MORTGAGE_DOCS_THRESHOLD } from '../../config/flowRules';
import { CSR_ASSISTANT_BLOCKED_STEPS, canActOnStep, completeStep, getFlowHistory, getStep, getStepData, moveToStep, saveStepData } from '../../services/flowService';
import { updateIssuedCheck } from '../../services/claimService';
import { listClosings } from '../../services/closingService';
import { openBackup, uploadClaimAttachment } from '../../services/backupService';
import { PAYMENT_TYPES, money } from '../../documents/closingData';
import StepForm from './StepForm';
import CheckFiles from './CheckFiles';
import ClosingBuilder from './ClosingBuilder';
import CsrCollection from './CsrCollection';
import { BlockerList, StepHelp, TimerBadge, TimerNotice, flowErrorMessage } from './FlowBits';

const ROUTE_STEPS = ['iink', 'mortgage_endorsement'];
const TRAMO_B = CHECK_FLOW.steps.filter(s => s.phase === 'B').map(s => s.id);

const detailsForm = check => ({
  amount: check.amount ?? '',
  checkNumber: check.checkNumber || '',
  checkDate: check.checkDate || '',
  paymentType: check.paymentType || '',
  payableTo: check.payableTo || '',
  hasMortgage: Boolean(check.hasMortgage),
  adjusterPercentage: check.adjusterPercentage ?? ''
});

// Los 3 documentos que el documento del cliente exige para mortgage > $40,000.
// stepData: el del paso actual; si el paso ya pasó, se carga acá.
const MortgageDocs = ({ check, claimRecord, stepData, canEdit, es, onSaved }) => {
  const [busyKey, setBusyKey] = useState('');
  const [ownData, setOwnData] = useState(null);
  useEffect(() => {
    if (stepData !== undefined) return;
    getStepData('check', check.id, 'mortgage_endorsement').then(setOwnData).catch(() => setOwnData({}));
  }, [check.id, stepData]);
  const required = (Number(check.amount) || 0) > MORTGAGE_DOCS_THRESHOLD;
  const docs = (stepData ?? ownData)?.docs || {};
  const upload = async (key, file) => {
    if (!file) return;
    setBusyKey(key);
    try {
      const stored = await uploadClaimAttachment(claimRecord.id, file);
      const ref = { storageKey: stored.storageKey, name: stored.name || file.name, uploadedAt: new Date().toISOString() };
      await saveStepData('check', check.id, 'mortgage_endorsement', { docs: { [key]: ref } });
      onSaved({ docs: { ...docs, [key]: ref } });
      toast.success(es ? 'Documento subido' : 'Document uploaded');
    } catch (error) {
      console.error('Mortgage doc upload failed:', error);
      toast.error(error.message);
    } finally {
      setBusyKey('');
    }
  };
  return (
    <div className="flow-block">
      <div className="flow-block-label">
        {es ? 'Documentos para el mortgage' : 'Mortgage documents'} · {required
          ? <span className="check-required">{es ? 'OBLIGATORIOS (cheque de más de $40,000)' : 'REQUIRED (check over $40,000)'}</span>
          : (es ? 'opcionales (cheque de $40,000 o menos)' : 'optional ($40,000 or less)')}
      </div>
      <div className="check-docs">
        {MORTGAGE_DOCS.map(d => {
          const doc = docs[d.key];
          return (
            <div key={d.key} className={`check-doc ${doc ? 'is-done' : required ? 'is-missing' : ''}`}>
              <span className="check-doc-icon"><i className={`fa-solid ${doc ? 'fa-circle-check' : 'fa-file-arrow-up'}`} aria-hidden="true"></i><span className="sr-only">{doc ? (es ? 'Subido: ' : 'Uploaded: ') : (required ? (es ? 'Falta: ' : 'Missing: ') : '')}</span></span>
              <span className="check-doc-name">{es ? d.es : d.en}</span>
              {doc && (
                <button type="button" className="cd-btn cd-btn-invoice" onClick={() => openBackup(doc.storageKey).catch(e => toast.error(e.message))}>
                  <i className="fa-solid fa-eye" aria-hidden="true"></i> {es ? 'Ver' : 'View'}
                </button>
              )}
              {canEdit && (
                <label className="cd-btn check-doc-upload">
                  <i className="fa-solid fa-upload" aria-hidden="true"></i> {busyKey === d.key ? (es ? 'Subiendo…' : 'Uploading…') : doc ? (es ? 'Reemplazar' : 'Replace') : (es ? 'Subir PDF' : 'Upload PDF')}
                  {/* Oculto a la vista pero enfocable: se activa con Tab + Enter/Espacio. */}
                  <input type="file" accept="application/pdf" className="visually-hidden-input" aria-label={`${es ? 'Subir' : 'Upload'} ${es ? d.es : d.en}`} disabled={Boolean(busyKey)} onChange={e => upload(d.key, e.target.files?.[0])} />
                </label>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Respaldos del cheque siempre a la vista, sin importar el paso en que esté:
// copia del cheque, enlace al CRM histórico y comprobantes de pago.
const CheckBackups = ({ check, es }) => (
  <div className="flow-block">
    <div className="flow-block-label">{es ? 'Respaldos del cheque' : 'Check backups'}</div>
    <CheckFiles check={check} es={es} />
  </div>
);

const CheckCard = ({ check: checkProp, claimRecord, isAdmin, language, prefill, onChange, defaultOpen = false }) => {
  const es = language === 'es';
  const t = pair => (es ? pair.es : pair.en);
  // CSR Assistant: mueve el cheque hasta antes del cobro de CSR; no ve lo que gana CSR (utils/permissions.js).
  const { userRoleKey, can } = useAuth();
  const isCsr = userRoleKey === 'csr_assistant';
  const role = isAdmin ? 'admin' : isCsr ? 'csr_assistant' : 'adjuster';
  const roleKey = isAdmin ? 'super_admin' : userRoleKey;
  const [check, setCheck] = useState(checkProp);
  const [open, setOpen] = useState(defaultOpen);
  const [history, setHistory] = useState([]);
  const [stepData, setStepData] = useState({});
  const [closing, setClosing] = useState(null);
  const [viewStepId, setViewStepId] = useState(null);
  const [editingDetails, setEditingDetails] = useState(null);
  const [busy, setBusy] = useState(false);
  const [move, setMove] = useState({ to: '', reason: '' });
  const fid = `chk-${checkProp.id}`;
  const [detailsError, setDetailsError] = useState('');
  // Al fallar la validación el foco pasa al campo con el error (3.3.1): el aviso solo no basta con teclado.
  const amountRef = useRef(null);
  const reasonRef = useRef(null);
  const [moveError, setMoveError] = useState('');

  useEffect(() => { setCheck(checkProp); }, [checkProp]);
  const currentId = check.flow?.stepId;
  const currentStep = getStep('check', currentId);

  const loadHistory = () => getFlowHistory('check', check.id).then(setHistory).catch(error => console.error('Error loading check history:', error));
  const loadClosing = () => listClosings(claimRecord.id)
    .then(list => setClosing(list.find(c => c.checkId === check.id) || null))
    .catch(error => console.error('Error loading closing:', error));
  useEffect(() => { if (open) { loadHistory(); loadClosing(); } }, [open, check.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // Si se avanza rápido, la respuesta del paso anterior no pisa la del actual.
  useEffect(() => {
    if (!currentId) return undefined;
    let cancelled = false;
    getStepData('check', check.id, currentId)
      .then(data => { if (!cancelled) setStepData(data); })
      .catch(error => { console.error('Error loading check step data:', error); if (!cancelled) setStepData({}); });
    return () => { cancelled = true; };
  }, [check.id, currentId]);

  const visited = useMemo(() => new Set(history.map(h => h.fromStep).filter(Boolean)), [history]);
  const stepStatus = id => (id === currentId ? 'current' : visited.has(id) ? 'done' : 'pending');
  const inTramoB = currentStep?.phase === 'B';

  // Línea de pasos: el tramo A, con la ruta de endoso elegida (o ninguna
  // mientras el PA no decide), y el cobro de CSR como un solo paso final.
  const stepperSteps = useMemo(() => {
    const route = ROUTE_STEPS.find(id => id === currentId || visited.has(id));
    return CHECK_FLOW.steps.filter(s => s.phase === 'A' && (!ROUTE_STEPS.includes(s.id) || s.id === route));
  }, [currentId, visited]);

  const context = { stepData, closing };
  const timer = getStepTimer('check', check, stepData);
  const blockers = currentStep ? getBlockers('check', check, context) : [];
  const canAct = canActOnStep(currentStep, role);
  const viewStep = getStep('check', viewStepId || currentId);
  const isViewingCurrent = viewStep?.id === currentId;

  const run = async (fn, okMessage) => {
    setBusy(true);
    try {
      const flow = await fn();
      const updated = { ...check, flow };
      setCheck(updated);
      setViewStepId(null);
      setStepData({});
      loadHistory();
      loadClosing();
      if (onChange) onChange(updated);
      toast.success(okMessage || (es ? `Cheque: ${getStep('check', flow.stepId).label.es}` : `Check: ${getStep('check', flow.stepId).label.en}`));
    } catch (error) {
      console.error('Check step failed:', error);
      toast.error(flowErrorMessage(error, es));
    } finally {
      setBusy(false);
    }
  };
  const complete = decisionId => run(() => completeStep({ type: 'check', recordId: check.id, currentStepId: currentId, decisionId, role, roleKey, context }));
  const doMove = () => {
    if (!move.to) return;
    if (!move.reason.trim()) { setMoveError(es ? 'Indica el motivo del cambio' : 'Enter a reason'); reasonRef.current?.focus(); return; }
    setMoveError('');
    run(() => moveToStep({ type: 'check', recordId: check.id, currentStepId: currentId ?? null, toStepId: move.to, reason: move.reason, role, roleKey }))
      .then(() => setMove({ to: '', reason: '' }));
  };

  const saveDetails = async () => {
    if (!(Number(editingDetails.amount) > 0)) { setDetailsError(es ? 'El monto del cheque es obligatorio' : 'Check amount is required'); amountRef.current?.focus(); return; }
    setDetailsError('');
    setBusy(true);
    try {
      const changes = await updateIssuedCheck(check, editingDetails);
      const updated = { ...check, ...changes };
      setCheck(updated);
      setEditingDetails(null);
      if (onChange) onChange(updated);
      toast.success(es ? 'Datos del cheque guardados' : 'Check details saved');
    } catch (error) {
      console.error('Error saving check:', error);
      toast.error(es ? 'No se pudieron guardar los datos del cheque' : 'Could not save the check');
    } finally {
      setBusy(false);
    }
  };

  // Quién puede editar los datos de un paso: el equipo CSR siempre; el PA
  // solo en los pasos que le tocan a él.
  const canEditStep = s => isAdmin || s.actor === 'pa' || (isCsr && s.actor === 'csr' && !CSR_ASSISTANT_BLOCKED_STEPS.includes(s.id));

  const stepBody = s => {
    const isCurrent = s.id === currentId;
    return (
      <div className="check-step-body">
        <div className="check-step-title">
          {t(s.label)}
          {!isCurrent && <span className="flow-muted"> · {stepStatus(s.id) === 'done' ? (es ? 'hecho' : 'done') : (es ? 'pendiente' : 'pending')}</span>}
        </div>
        <StepHelp help={STEP_HELP.check[s.id]} es={es} />
        {isCurrent && <TimerNotice timer={timer} es={es} />}

        {s.modules.includes('stepForm') && s.fields?.length > 0 && (
          <StepForm type="check" record={check} step={s} language={language} readOnly={!canEditStep(s)}
            onSaved={(_, stepChanges) => { if (isCurrent) setStepData(prev => ({ ...prev, ...stepChanges })); }} />
        )}
        {s.modules.includes('mortgageDocs') && (
          <MortgageDocs check={check} claimRecord={claimRecord} stepData={isCurrent ? stepData : undefined} canEdit={isAdmin || isCsr} es={es}
            onSaved={changes => setStepData(prev => ({ ...prev, ...changes }))} />
        )}
        {s.modules.includes('closing') && (
          <ClosingBuilder key={s.id} claimRecord={claimRecord} language={language} isAdmin={isAdmin || isCsr} check={check} prefill={prefill}
            mode={isCurrent ? 'full' : 'list'}
            canEdit={isCurrent && (s.id === 'breakdown' || isAdmin || isCsr)}
            onClosingsChange={list => setClosing(list[0] || null)} />
        )}
        {(s.modules.includes('receiveCheck') || s.modules.includes('checkPayment')) && can('viewEarnings') && (
          <CsrCollection check={check} isAdmin={isAdmin} language={language}
            onCheckChange={fresh => { const updated = { ...check, ...fresh }; setCheck(updated); if (onChange) onChange(updated); }}
            onPaid={() => (currentId !== 'check_archived'
              ? run(() => moveToStep({ type: 'check', recordId: check.id, currentStepId: currentId, toStepId: 'check_archived', reason: es ? 'Comisión cobrada completa' : 'Commission fully collected', role, roleKey }))
              : null)} />
        )}

        {isCurrent && s.kind !== 'final' && (
          canAct ? (
            <>
              <BlockerList blockers={blockers} es={es} />
              <div className="flow-actions">
                {s.options
                  ? s.options.map(option => {
                    const optionBlockers = getOptionBlockers('check', check, option.id, context);
                    return (
                      <button key={option.id} type="button" className="cd-btn cd-btn-check" disabled={busy || blockers.length > 0 || optionBlockers.length > 0}
                        title={optionBlockers.map(b => (es ? b.es : b.en)).join(' ')} onClick={() => complete(option.id)}>
                        {t(option.label)}
                      </button>
                    );
                  })
                  : (
                    <button type="button" className="cd-btn cd-btn-check" disabled={busy || blockers.length > 0} onClick={() => complete()}>
                      <i className="fa-solid fa-check" aria-hidden="true"></i> {es ? 'Listo, siguiente' : 'Done, next'}: {t(getStep('check', s.next).label)}
                    </button>
                  )}
              </div>
              {s.options && s.options.map(option => {
                const optionBlockers = getOptionBlockers('check', check, option.id, context);
                return optionBlockers.length > 0
                  ? <p key={option.id} className="flow-note"><strong>{t(option.label)}:</strong> {optionBlockers.map(b => (es ? b.es : b.en)).join(' ')}</p>
                  : null;
              })}
            </>
          ) : (
            <p className="flow-note">{s.actor === 'pa' ? (es ? 'Este paso lo decide el PA.' : 'The PA decides this step.') : (es ? 'Este paso lo registra el equipo CSR.' : 'The CSR team records this step.')}</p>
          )
        )}
      </div>
    );
  };

  const statusLabel = !currentStep
    ? (es ? 'Anterior al flujo de pasos' : 'Before the step flow')
    : inTramoB ? (es ? `Entregado · cobro CSR: ${currentStep.label.es}` : `Delivered · CSR: ${currentStep.label.en}`) : t(currentStep.label);

  return (
    <div className={`check-card ${open ? 'is-open' : ''}`}>
      <button type="button" className="check-card-head" onClick={() => setOpen(v => !v)} aria-expanded={open}>
        <span className="check-card-icon"><i className="fa-solid fa-money-check-dollar" aria-hidden="true"></i></span>
        <span className="check-card-main">
          <span className="check-card-title">
            {money(check.amount) || '$0'} · {check.checkNumber ? `${es ? 'Cheque' : 'Check'} #${check.checkNumber}` : (es ? 'Cheque sin número' : 'Check without number')}
          </span>
          <span className="check-card-meta">{[check.paymentType, check.checkDate && `${es ? 'emitido' : 'issued'} ${check.checkDate}`, check.hasMortgage ? 'Mortgage' : null].filter(Boolean).join(' · ')}</span>
        </span>
        <span className="check-card-status">
          <span className={`cd-status-badge ${inTramoB ? 'cd-status-paid' : 'cd-status-count'}`}>{statusLabel}</span>
          {timer && <TimerBadge timer={timer} es={es} />}
        </span>
        <i className={`fa-solid fa-chevron-${open ? 'up' : 'down'} flow-step-chevron`} aria-hidden="true"></i>
      </button>

      {open && (
        <div className="check-card-body">
          {/* Datos del cheque */}
          {editingDetails ? (
            <div className="flow-subpanel">
              <div className="flow-subpanel-title">{es ? 'Datos del cheque' : 'Check details'}</div>
              <div className="cc-grid">
                <div className="cc-input-group">
                  <label className="cc-label" htmlFor={`${fid}-amount`}>{es ? 'Monto del cheque *' : 'Check amount *'}</label>
                  <input id={`${fid}-amount`} ref={amountRef} type="number" step="0.01" min="0" className="cc-input" required aria-required="true"
                    aria-invalid={detailsError ? 'true' : undefined} aria-describedby={detailsError ? `${fid}-amount-error` : undefined}
                    value={editingDetails.amount} onChange={e => { setDetailsError(''); setEditingDetails(d => ({ ...d, amount: e.target.value })); }} />
                  {detailsError && <p id={`${fid}-amount-error`} role="alert" className="cc-field-error">{detailsError}</p>}
                </div>
                <div className="cc-input-group">
                  <label className="cc-label" htmlFor={`${fid}-number`}>{es ? 'Número del cheque' : 'Check number'}</label>
                  <input id={`${fid}-number`} className="cc-input" value={editingDetails.checkNumber} onChange={e => setEditingDetails(d => ({ ...d, checkNumber: e.target.value }))} />
                </div>
                <div className="cc-input-group">
                  <label className="cc-label" htmlFor={`${fid}-date`}>{es ? 'Fecha de emisión' : 'Issue date'}</label>
                  <input id={`${fid}-date`} type="date" className="cc-input" value={editingDetails.checkDate} onChange={e => setEditingDetails(d => ({ ...d, checkDate: e.target.value }))} />
                </div>
                <div className="cc-input-group">
                  <label className="cc-label" htmlFor={`${fid}-payment`}>{es ? 'Tipo de pago' : 'Payment type'}</label>
                  <select id={`${fid}-payment`} className="cc-select" value={editingDetails.paymentType} onChange={e => setEditingDetails(d => ({ ...d, paymentType: e.target.value }))}>
                    <option value="">—</option>
                    {PAYMENT_TYPES.map(p => <option key={p.value} value={p.value}>{es ? p.es : p.value}</option>)}
                  </select>
                </div>
                <div className="cc-input-group">
                  <label className="cc-label" htmlFor={`${fid}-payable`}>{es ? 'A nombre de (payable to)' : 'Payable to'}</label>
                  <input id={`${fid}-payable`} className="cc-input" value={editingDetails.payableTo} onChange={e => setEditingDetails(d => ({ ...d, payableTo: e.target.value }))} />
                </div>
                <div className="cc-input-group">
                  <label className="cc-label" htmlFor={`${fid}-pa`}>{es ? '% del PA' : 'PA %'}</label>
                  <input id={`${fid}-pa`} type="number" step="0.5" min="0" max="100" className="cc-input" value={editingDetails.adjusterPercentage} onChange={e => setEditingDetails(d => ({ ...d, adjusterPercentage: e.target.value }))} />
                </div>
              </div>
              <label className="check-toggle">
                <input type="checkbox" checked={editingDetails.hasMortgage} onChange={e => setEditingDetails(d => ({ ...d, hasMortgage: e.target.checked }))} />
                {es ? 'El cheque incluye al mortgage (necesita endoso del banco)' : 'The check includes the mortgage (needs bank endorsement)'}
              </label>
              <div className="flow-actions">
                <button type="button" className="cd-btn cd-btn-check" disabled={busy} onClick={saveDetails}><i className="fa-solid fa-floppy-disk" aria-hidden="true"></i> {es ? 'Guardar' : 'Save'}</button>
                <button type="button" className="cd-btn" onClick={() => { setDetailsError(''); setEditingDetails(null); }}>{es ? 'Cancelar' : 'Cancel'}</button>
              </div>
            </div>
          ) : (
            <div className="check-details">
              <span>{es ? 'Monto' : 'Amount'}<strong>{money(check.amount) || '—'}</strong></span>
              <span>{es ? 'Número' : 'Number'}<strong>{check.checkNumber || '—'}</strong></span>
              <span>{es ? 'Emitido' : 'Issued'}<strong>{check.checkDate || '—'}</strong></span>
              <span>{es ? 'A nombre de' : 'Payable to'}<strong>{check.payableTo || '—'}</strong></span>
              <span>Mortgage<strong>{check.hasMortgage ? (es ? 'Sí' : 'Yes') : 'No'}</strong></span>
              {check.adjusterPercentage !== undefined && <span>{es ? '% PA' : 'PA %'}<strong>{check.adjusterPercentage ? `${check.adjusterPercentage}%` : '—'}</strong></span>}
              {isAdmin && (
                <button type="button" className="cd-btn check-details-edit" onClick={() => setEditingDetails(detailsForm(check))}>
                  <i className="fa-solid fa-pen" aria-hidden="true"></i> {es ? 'Editar' : 'Edit'}
                </button>
              )}
            </div>
          )}

          <CheckBackups check={check} es={es} />

          {currentStep ? (
            <>
              {/* Línea de pasos: cualquier paso se abre para ver o corregir sus datos */}
              <ol className="check-stepper" aria-label={es ? 'Pasos del cheque' : 'Check steps'}>
                {stepperSteps.map((s, index) => {
                  // Lo anterior al paso actual en la línea ya se hizo (aunque el
                  // cheque haya entrado directo a un paso, p. ej. migrado).
                  const currentIndex = stepperSteps.findIndex(x => x.id === currentId);
                  const status = inTramoB || (currentIndex > -1 && index < currentIndex) ? 'done' : stepStatus(s.id);
                  return (
                    <li key={s.id} className={`is-${status} ${viewStep?.id === s.id ? 'is-viewing' : ''}`}>
                      <button type="button" onClick={() => setViewStepId(s.id === currentId ? null : s.id)} aria-current={status === 'current' ? 'step' : undefined}
                        aria-label={`${index + 1}. ${t(s.label)} — ${{ done: es ? 'hecho' : 'done', current: es ? 'paso actual' : 'current step', pending: es ? 'pendiente' : 'pending' }[status]}`}>
                        <span className="check-stepper-dot">{status === 'done' ? <i className="fa-solid fa-check" aria-hidden="true"></i> : index + 1}</span>
                        <span className="check-stepper-label">{t(s.label).replace(/ \(.*\)$/, '')}</span>
                        <span className="check-stepper-state">{{ done: es ? 'Hecho' : 'Done', current: es ? 'Actual' : 'Current', pending: es ? 'Pendiente' : 'Pending' }[status]}</span>
                      </button>
                    </li>
                  );
                })}
                <li className={`is-${inTramoB ? (currentId === 'check_archived' ? 'done' : 'current') : 'pending'} ${TRAMO_B.includes(viewStep?.id) ? 'is-viewing' : ''}`}>
                  <button type="button" onClick={() => setViewStepId(inTramoB ? null : 'real_values')} aria-current={inTramoB && currentId !== 'check_archived' ? 'step' : undefined}>
                    <span className="check-stepper-dot">{currentId === 'check_archived' ? <i className="fa-solid fa-check" aria-hidden="true"></i> : stepperSteps.length + 1}</span>
                    <span className="check-stepper-label">{es ? 'Cobro CSR' : 'CSR collection'}</span>
                    <span className="check-stepper-state">{(inTramoB ? (currentId === 'check_archived' ? 'done' : 'current') : 'pending') === 'done' ? (es ? 'Hecho' : 'Done') : inTramoB ? (es ? 'Actual' : 'Current') : (es ? 'Pendiente' : 'Pending')}</span>
                  </button>
                </li>
              </ol>

              {viewStep && (
                <div className={`check-step ${isViewingCurrent ? 'is-current' : ''}`}>
                  {stepBody(viewStep)}
                  {!isViewingCurrent && (
                    <button type="button" className="flow-history-toggle" onClick={() => setViewStepId(null)}>
                      <i className="fa-solid fa-arrow-left" aria-hidden="true"></i> {es ? 'Volver al paso actual' : 'Back to the current step'}
                    </button>
                  )}
                </div>
              )}

              {isAdmin && (
                <details className="flow-advanced">
                  <summary>{es ? 'Avanzado: corregir el paso del cheque' : 'Advanced: fix the check step'}</summary>
                  <div className="flow-move">
                    <select className="cc-select" value={move.to} onChange={e => setMove(m => ({ ...m, to: e.target.value }))} aria-label={es ? 'Mover el cheque a otro paso' : 'Move check to another step'}>
                      <option value="">{es ? 'Mover el cheque a otro paso…' : 'Move check to another step…'}</option>
                      {CHECK_FLOW.steps.map(s => <option key={s.id} value={s.id}>{t(s.label)}</option>)}
                    </select>
                    {move.to && (
                      <>
                        <input id={`${fid}-move-reason`} ref={reasonRef} className="cc-input" required aria-required="true" aria-label={es ? 'Motivo del cambio (obligatorio)' : 'Reason for the change (required)'}
                          aria-invalid={moveError ? 'true' : undefined} aria-describedby={moveError ? `${fid}-move-error` : undefined}
                          placeholder={es ? 'Motivo (obligatorio)' : 'Reason (required)'} value={move.reason}
                          onChange={e => { setMoveError(''); setMove(m => ({ ...m, reason: e.target.value })); }} />
                        <button type="button" className="cd-btn" disabled={busy} onClick={doMove}>{es ? 'Mover' : 'Move'}</button>
                        {moveError && <p id={`${fid}-move-error`} role="alert" className="cc-field-error">{moveError}</p>}
                      </>
                    )}
                  </div>
                </details>
              )}
            </>
          ) : (
            <p className="flow-note">{es ? 'Este cheque es anterior al flujo de pasos: su cobro se sigue como antes, desde la lista de cheques.' : 'This check predates the step flow.'}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default CheckCard;
